import type { Role } from '@/features/auth/api';

/**
 * ロール別の画面配色（D-101）。
 *
 * 5ロールとも同じ見た目だったため、「今どの立場でログインしているのか」が
 * 一目で分からなかった（複数ロールを持つ人・事務局が受講生の画面を確認するときに特に困る）。
 * サイドバーの地色と現在地の色をロールごとに変え、上部にロール名の帯を出す。
 *
 * 色は **docs/TEST-MANUAL.html のロールバッジと同じ**ものを正とする
 * （資料と画面で色が違うと、かえって混乱するため）。
 * 地色は暗いトーンに寄せて、白文字のコントラストを確保している。
 */
export interface RoleTheme {
  /** サイドバーの地色 */
  sidebar: string;
  /** サイドバーのホバー・展開時の地色 */
  sidebarHover: string;
  /** 上部の帯・現在地の縦線に使う色（資料のロールバッジと同色） */
  accent: string;
  /** 帯の上に置く文字色（accent とのコントラストで選ぶ） */
  accentText: string;
  /** 画面に出すロール名 */
  label: string;
}

export const ROLE_THEMES: Record<Role, RoleTheme> = {
  student: {
    sidebar: '#11241a',
    sidebarHover: '#1b3527',
    accent: '#00a32a',
    accentText: '#ffffff',
    label: '受講生',
  },
  director: {
    sidebar: '#241d10',
    sidebarHover: '#352c1a',
    accent: '#996800',
    accentText: '#ffffff',
    label: '理事・講師',
  },
  success: {
    sidebar: '#1d1428',
    sidebarHover: '#2b1f3a',
    accent: '#7b2cbf',
    accentText: '#ffffff',
    label: 'サクセス担当',
  },
  office: {
    // 事務局は最も使う画面なので、従来の配色（WP管理画面の濃紺）を維持する
    sidebar: '#1d2327',
    sidebarHover: '#2c3338',
    accent: '#2271b1',
    accentText: '#ffffff',
    label: '事務局',
  },
  super_admin: {
    sidebar: '#2a1416',
    sidebarHover: '#3b1e21',
    accent: '#d63638',
    accentText: '#ffffff',
    label: 'スーパー管理者',
  },
};

export function themeFor(role: Role): RoleTheme {
  return ROLE_THEMES[role] ?? ROLE_THEMES.office;
}
